import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logErrorToast } from '@/lib/errorLogger';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export function useUserProfile() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Load profile on mount
  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('display_name, avatar_url')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setDisplayName(data.display_name || '');
          setAvatarUrl(data.avatar_url || null);
        }
      });
  }, [user]);

  const loadProfile = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('display_name, avatar_url')
      .eq('id', user.id)
      .single();
    if (data) {
      setDisplayName(data.display_name || '');
      setAvatarUrl(data.avatar_url || null);
    }
  }, [user]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (fileInputRef.current) fileInputRef.current.value = '';

    if (!ALLOWED_TYPES.includes(file.type)) {
      logErrorToast('Please upload a JPG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      logErrorToast('Image must be under 2MB.');
      return;
    }

    setUploading(true);

    try {
      const base64 = await fileToBase64(file);

      const { data: modResult, error: modError } = await supabase.functions.invoke('moderate-image', {
        body: { imageBase64: base64, mimeType: file.type },
      });

      if (modError) {
        console.error('Moderation error:', modError);
        logErrorToast('Unable to verify image. Please try again.');
        setUploading(false);
        return;
      }

      if (!modResult?.safe) {
        logErrorToast(modResult?.reason || 'This image was flagged as inappropriate. Please choose a different photo.');
        setUploading(false);
        return;
      }

      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const filePath = `${user.id}/avatar.${ext}`;

      const { data: existingFiles } = await supabase.storage
        .from('avatars')
        .list(user.id);

      if (existingFiles?.length) {
        await supabase.storage
          .from('avatars')
          .remove(existingFiles.map(f => `${user.id}/${f.name}`));
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true, contentType: file.type });

      if (uploadError) {
        logErrorToast('Upload failed. Please try again.');
        setUploading(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);

      if (profileError) {
        logErrorToast('Failed to save avatar.');
      } else {
        setAvatarUrl(publicUrl);
        toast.success('Avatar updated!');
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      logErrorToast('Something went wrong. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const initials = displayName
    ? displayName.slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || '??';

  return {
    user,
    displayName,
    avatarUrl,
    uploading,
    initials,
    fileInputRef,
    loadProfile,
    handleAvatarUpload,
  };
}
